import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Link, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import BeliefsPage from "@/pages/BeliefsPage";
import StoriesPage from "@/pages/StoriesPage";
import GapsPage from "@/pages/GapsPage";
import BeliefDetailPage from "@/pages/BeliefDetailPage";
import DashboardPage from "@/pages/DashboardPage";
import WriteBeliefsPage from "@/pages/WriteBeliefsPage";
import CreateContentPage from "@/pages/CreateContentPage";
import ContentLibraryPage from "@/pages/ContentLibraryPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import LoginPage from "@/pages/LoginPage";
import AccountPage from "@/pages/AccountPage";
import PromotionFocusPage from "@/pages/PromotionFocusPage";
import { SyncButton } from "@/components/SyncButton";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LogOut, User } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center bg-[#F2E9DB] text-sm text-[#141B4D]/60"
        style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
      >
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}

function Nav() {
  const { user, logout } = useAuth();
  const linkBase =
    "px-3 py-2 text-sm font-medium transition-colors text-[#141B4D]/70 hover:text-[#141B4D]";
  const linkActive = "text-[#141B4D] font-bold border-b-2 border-[#141B4D]";
  return (
    <header
      className="border-b border-[#141B4D]/15 bg-[#F2E9DB] sticky top-0 z-50"
      data-testid="app-nav"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0">
          <span className="inline-block w-2 h-6 bg-[#FFD100]" />
          <span className="text-[#141B4D] font-bold tracking-tight text-base">
            Indie Law · Content Engine
          </span>
        </Link>
        <nav className="flex items-center gap-0.5 flex-wrap">
          <NavLink end to="/" data-testid="nav-dashboard" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Dashboard</NavLink>
          <NavLink to="/beliefs" data-testid="nav-beliefs" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Beliefs</NavLink>
          <NavLink to="/write-beliefs" data-testid="nav-write-beliefs" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Write Beliefs</NavLink>
          <NavLink to="/stories" data-testid="nav-stories" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Stories</NavLink>
          <NavLink to="/promotion-focus" data-testid="nav-promotion-focus" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Promotion Focus</NavLink>
          <NavLink to="/gaps" data-testid="nav-gaps" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Gaps</NavLink>
          <NavLink to="/create" data-testid="nav-create-content" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Create</NavLink>
          <NavLink to="/library" data-testid="nav-library" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Library</NavLink>
          <NavLink to="/integrations" data-testid="nav-integrations" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}>Integrations</NavLink>
          <SyncButton />
          {user && (
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[#141B4D]/15">
              <NavLink
                to="/account"
                data-testid="nav-account"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-2.5 py-2 text-xs ${isActive ? "text-[#141B4D] font-bold" : "text-[#141B4D]/70 hover:text-[#141B4D]"}`
                }
                title={user.email}
              >
                <User size={12} strokeWidth={2.5} />
                <span className="hidden lg:inline">{user.email.split("@")[0]}</span>
              </NavLink>
              <button
                onClick={logout}
                data-testid="nav-logout-button"
                title="Sign out"
                className="inline-flex items-center justify-center p-2 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
              >
                <LogOut size={13} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

function AppShell() {
  return (
    <>
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/beliefs" element={<BeliefsPage />} />
          <Route path="/beliefs/:id" element={<BeliefDetailPage />} />
          <Route path="/write-beliefs" element={<WriteBeliefsPage />} />
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/promotion-focus" element={<PromotionFocusPage />} />
          <Route path="/gaps" element={<GapsPage />} />
          <Route path="/create" element={<CreateContentPage />} />
          <Route path="/library" element={<ContentLibraryPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/account" element={<AccountPage />} />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <div
      className="App min-h-screen bg-[#F2E9DB] text-[#141B4D]"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              fontFamily: "Arial, Helvetica, sans-serif",
              background: "#141B4D",
              color: "#F2E9DB",
              border: "none",
              borderRadius: "2px",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
